import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useTenants } from '../../lib/api/hooks.js';
import { useCallPlans } from '../../lib/api/hooks-products.js';
import { useCreateTenant } from '../../lib/api/hooks-billing.js';
import { StatusBadge, TableSkeleton, Empty, ErrorBanner, SubHead } from '../../components/ui/Primitives.js';
import { useSession } from '../../lib/auth/session.js';
import { ApiClientError } from '../../lib/api/client.js';

/**
 * The account list — the console's home for day-to-day work.
 *
 * A row is a link into that account, not a selection. Everything about one
 * customer lives behind one URL, so support can paste a link and land exactly
 * where the caller is.
 */
export function AccountsPage() {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [adding, setAdding] = useState(false);
  const can = useSession((s) => s.can);
  const { data, isLoading, error } = useTenants({
    q: q || undefined, status: status || undefined, limit: 100,
  });

  return (
    <>
      <div className="arc-page-head">
        <div>
          <h1 className="arc-h1">Customer accounts</h1>
          <p className="arc-muted" style={{ marginTop: 3 }}>
            Every PBX on the platform. Open one to manage its extensions, trunks,
            numbers and billing.
          </p>
        </div>
        {can('tenant:create') && (
          <button type="button" className="arc-btn arc-btn-primary" onClick={() => setAdding((v) => !v)}>
            {adding ? 'Cancel' : '+ New account'}
          </button>
        )}
      </div>

      {error && <ErrorBanner error={error} />}
      {adding && <NewAccountForm onDone={() => setAdding(false)} />}

      <div className="arc-card">
        <div className="arc-toolbar">
          <div className="arc-search">
            <input className="arc-input" placeholder="Search by company or slug…"
              value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search accounts" />
          </div>
          <select className="arc-select" style={{ width: 'auto' }} value={status}
            onChange={(e) => setStatus(e.target.value)} aria-label="Filter by status">
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="provisioning">Provisioning</option>
            <option value="suspended">Suspended</option>
            <option value="closed">Closed</option>
          </select>
          <div className="arc-spacer" />
          <span className="arc-subtle arc-num" style={{ fontSize: 12.5 }}>
            {data ? `${data.data.length} account${data.data.length === 1 ? '' : 's'}` : ''}
          </span>
        </div>

        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr>
                <th>Account</th><th>SIP domain</th><th>Status</th>
                <th className="arc-td-right">Ext limit</th>
                <th className="arc-td-right">Channels</th>
                <th className="arc-td-right">CPS</th>
                <th>Timezone</th><th />
              </tr>
            </thead>
            {isLoading ? <TableSkeleton cols={8} /> : (
              <tbody>
                {data?.data.map((t) => (
                  <tr key={t.id}>
                    <td>
                      <Link to="/accounts/$accountId" params={{ accountId: t.slug }} className="arc-stack">
                        <span style={{ fontWeight: 570, color: 'var(--arc-accent)' }}>{t.name}</span>
                        <span className="arc-subtle" style={{ fontSize: 12 }}>{t.slug}</span>
                      </Link>
                    </td>
                    <td className="arc-mono arc-subtle">{t.sip_domain}</td>
                    <td><StatusBadge status={t.status} /></td>
                    <td className="arc-td-right arc-num">{t.max_extensions ?? '—'}</td>
                    <td className="arc-td-right arc-num">{t.max_concurrent_calls}</td>
                    <td className="arc-td-right arc-num">{t.max_cps}</td>
                    <td className="arc-muted" style={{ fontSize: 12.5 }}>{t.timezone}</td>
                    <td className="arc-td-right">
                      <Link to="/accounts/$accountId" params={{ accountId: t.slug }}
                        className="arc-btn arc-btn-ghost arc-btn-sm">
                        Open →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>

        {data !== undefined && data?.data.length === 0 && (
          <Empty icon="accounts" title="No accounts found"
            hint={q ? 'Try a different search term.' : 'Create the first customer account.'} />
        )}
      </div>
    </>
  );
}

function NewAccountForm({ onDone }: { onDone: () => void }) {
  const create = useCreateTenant();
  const plans = useCallPlans();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [domain, setDomain] = useState('');
  const [planId, setPlanId] = useState('');
  const [touched, setTouched] = useState(false);

  const onName = (v: string) => {
    setName(v);
    if (!touched) {
      const s = v.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40);
      setSlug(s);
      setDomain(s ? `${s}.arcnet.arcline.dev` : '');
    }
  };

  const err = create.error instanceof ApiClientError ? create.error : null;
  const fieldErrors = err?.fieldErrors ?? {};

  return (
    <form
      className="arc-card arc-card-body" style={{ marginBottom: 16 }}
      onSubmit={(e) => {
        e.preventDefault();
        create.mutate(
          {
            name, slug, sip_domain: domain,
            timezone: 'Africa/Johannesburg', country_code: 'ZA', currency: 'ZAR',
          },
          { onSuccess: () => { setName(''); setSlug(''); setDomain(''); onDone(); } },
        );
      }}
    >
      <SubHead>New customer account</SubHead>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr auto', gap: 12, alignItems: 'start' }}>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="a-name">Company name</label>
          <input id="a-name" className="arc-input" required value={name}
            onChange={(e) => onName(e.target.value)} placeholder="Meridian Logistics" />
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="a-slug">Slug</label>
          <input id="a-slug" className="arc-input" required value={slug}
            onChange={(e) => { setTouched(true); setSlug(e.target.value); }}
            aria-invalid={!!fieldErrors['slug']} />
          {fieldErrors['slug'] && <div className="arc-error">{fieldErrors['slug']}</div>}
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="a-domain">SIP domain</label>
          <input id="a-domain" className="arc-input" required value={domain}
            onChange={(e) => setDomain(e.target.value)} aria-invalid={!!fieldErrors['sip_domain']} />
          {/* ADR-002/013: HA1 credentials are bound to the realm, so changing
              this later de-registers every handset. The database refuses the
              UPDATE; saying so here prevents the attempt. */}
          <div className="arc-hint" style={{ color: 'var(--arc-warn)' }}>
            Permanent — cannot be changed later.
          </div>
          {fieldErrors['sip_domain'] && <div className="arc-error">{fieldErrors['sip_domain']}</div>}
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="a-plan">Call plan</label>
          <select id="a-plan" className="arc-select" value={planId}
            onChange={(e) => setPlanId(e.target.value)}>
            <option value="">Assign later</option>
            {plans.data?.data.filter((p) => p.is_active).map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
        <div style={{ paddingTop: 25 }}>
          <button type="submit" className="arc-btn arc-btn-primary" disabled={create.isPending}>
            {create.isPending ? 'Creating…' : 'Create'}
          </button>
        </div>
      </div>
    </form>
  );
}
